import type { SpecType } from "@/api/contracts/specifications";
import { MuiInfoOutlinedMediumIcon } from "@/components/ui/mui-spec-icons";
import { LEN_TOOLTIP_DELAY_MS, MuiTooltip } from "@/components/ui/mui-tooltip";
import { ancestorName } from "@/lib/spec-base-changes";
import { cn } from "@/lib/utils";

/** The donor's fixed sentence — the icon's until its caller names the fields. */
function defaultSentence(specType: SpecType) {
  return `This Line has been modified from original ${ancestorName(specType)} Specification.`;
}

/**
 * The info glyph on a changed row — `ChangedFromBase`
 * (`shared/components/ChangedFromBase.tsx`): 24px in `changedFromBaseIcon`,
 * inside the modsy tooltip with its callout, below. Shared, as the donor's is
 * (`shared/components/`): the details rows and the Editor's rows draw it (#809).
 *
 * **A button, not the donor's bare glyph** (#1083; ledger §7, "Changed from
 * base widens"): a keyboard reaches it, its tooltip opens on focus as on
 * hover, and its accessible name is the tooltip's sentence. No pixel moves
 * but the focus ring: it takes its row's cursor, as the glyph did (the
 * Editor's pointer), and a press on it still lifts the Editor's row
 * (`data-dnd-press-lifts`). Its click still reaches the row under it, as the
 * donor's glyph's does: the Editor's row makes its line Active on it. The
 * sentence names what changed when the caller passes one
 * (`rowChangeSentence`); without one it is the donor's fixed sentence.
 */
export function ChangedFromBase({
  specType,
  sentence,
  className,
}: {
  specType: SpecType;
  /** What the tooltip and the accessible name say; the donor's sentence unset. */
  sentence?: string;
  className?: string;
}) {
  const text = sentence ?? defaultSentence(specType);
  return (
    <MuiTooltip title={text} delayMs={LEN_TOOLTIP_DELAY_MS} side="bottom" arrow asChild>
      <button
        type="button"
        data-testid="info-icon"
        // A press lifts the Editor's row from it, as from the donor's glyph.
        data-dnd-press-lifts=""
        aria-label={text}
        className={cn(
          "text-spec-changed-from-base-icon inline-flex cursor-[inherit] border-0 bg-transparent p-0",
          "focus-visible:outline-lennar-blue-600 rounded-full outline-none focus-visible:outline-2 focus-visible:outline-solid",
          className,
        )}
      >
        <MuiInfoOutlinedMediumIcon />
      </button>
    </MuiTooltip>
  );
}
